// FIX-AUTH-NETWORK-ERROR (SD-437) — is the stored session still valid?
//
// A session is INVALID only when the server says so:
//   - validate_session answers is_valid = false (the token is unknown, expired or revoked), or
//   - the request is refused as unauthorised (HTTP 401 / 403).
// A check that could not be completed — no signal, a dropped connection (supabase-js returns these as `error` with
// status 0, it does not throw), a timeout or a 5xx — is UNKNOWN, and the session is kept. Before this fix any such
// failure at the moment the phone came back online logged the farmer out. Every server call still enforces the token.

import { supabase } from '@/integrations/supabase/client';
import { recordServerNow } from '@/shared/lib/serverClock';

type RpcResult = { data: unknown; error: { message?: string } | null; status?: number };
export type ValidateRpc = (token: string) => Promise<RpcResult>;

const defaultRpc: ValidateRpc = async (token) => {
  const res = await supabase.rpc('validate_session', { session_token: token });
  return { data: res.data as unknown, error: res.error, status: res.status };
};

export async function validateSession(token: string, rpc: ValidateRpc = defaultRpc, online: () => boolean = () => navigator.onLine): Promise<boolean> {
  if (!online()) return true; // offline: cannot check — revalidated on reconnect
  try {
    const { data, error, status } = await rpc(token);
    if (error) return !(status === 401 || status === 403); // refused as unauthorised → invalid; anything else → unknown
    if (!data) return false;
    // A2 (R17): measure the phone's clock gap from the server's reply (additive field).
    const row = Array.isArray(data) ? (data[0] as { server_now?: string; is_valid?: boolean } | undefined) : undefined;
    recordServerNow(row?.server_now, 'validate_session');
    return Array.isArray(data) ? row?.is_valid === true : data === true;
  } catch {
    return true; // the check itself failed (network) → unknown, keep the session
  }
}
